import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';

const AuthField = ({ label, error, icon: Icon, children }) => (
  <label className="block">
    <span className="gadkot-auth__label trek-marathi-text">{label}</span>
    <div className="relative">
      <Icon className="gadkot-auth__field-icon" aria-hidden="true" />
      {children}
    </div>
    {error && (
      <p className="gadkot-auth__error trek-marathi-text" role="alert">
        {error}
      </p>
    )}
  </label>
);

const AuthModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const { login, isLoading } = useAuth();

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setFormData({ email: '', password: '' });
    setShowPassword(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const validateForm = () => {
    const next = {};
    if (!formData.email.trim()) next.email = t('auth.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) next.email = t('auth.emailInvalid');
    if (!formData.password) next.password = t('auth.passwordRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const result = await login(formData.email.trim(), formData.password);
      if (result.success) {
        onClose();
        setFormData({ email: '', password: '' });
      } else {
        setErrors({ general: result.error });
      }
    } catch {
      setErrors({ general: t('auth.generalError') });
    }
  };

  const modal = (
    <div
      className="gadkot-auth__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="gadkot-auth__panel" onClick={(e) => e.stopPropagation()}>
        <div className="gadkot-auth__header">
          <div className="gadkot-auth__header-inner">
            <div className="gadkot-auth__seal" aria-hidden="true">
              <Shield className="h-7 w-7 text-amber-200/90" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="gadkot-auth__badge trek-marathi-text">{t('auth.modalBadge')}</p>
              <h2 id="auth-modal-title" className="gadkot-auth__title font-marathi-display">
                {t('auth.signIn')}
              </h2>
              <p className="gadkot-auth__subtitle trek-marathi-text">{t('auth.signInSubtitle')}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="gadkot-auth__close"
              aria-label={t('ui.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="gadkot-auth__body">
          {errors.general && (
            <div className="gadkot-auth__alert trek-marathi-text" role="alert">
              {errors.general}
            </div>
          )}

          <AuthField label={t('auth.email')} error={errors.email} icon={Mail}>
            <input
              type="email"
              autoComplete="email"
              name="email"
              id="auth-modal-email"
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
                if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              className={`trek-input gadkot-auth__input w-full rounded-xl py-3 pl-11 pr-4 ${
                errors.email ? 'gadkot-auth__input--error' : ''
              }`}
              placeholder={t('auth.emailPlaceholder')}
            />
          </AuthField>

          <AuthField label={t('auth.password')} error={errors.password} icon={Lock}>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              name="password"
              id="auth-modal-password"
              value={formData.password}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, password: e.target.value }));
                if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
              }}
              className={`trek-input gadkot-auth__input w-full rounded-xl py-3 pl-11 pr-12 ${
                errors.password ? 'gadkot-auth__input--error' : ''
              }`}
              placeholder={t('auth.passwordPlaceholder')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="gadkot-auth__eye-btn"
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </AuthField>

          <button
            type="submit"
            disabled={isLoading}
            className="trek-btn-primary gadkot-auth__submit trek-marathi-text w-full rounded-xl py-3.5 font-bold disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        <div className="gadkot-auth__footer trek-marathi-text">
          <p>
            {t('auth.noAccount')}{' '}
            <Link to="/register" onClick={onClose} className="gadkot-auth__footer-link">
              {t('auth.registerLink')}
            </Link>
          </p>
          <p className="gadkot-auth__footer-hint">{t('auth.registerMenuDesc')}</p>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AuthModal;
